import type {
  AddPackageCustomerRequest,
  CreatePackageRequest,
  DeletePackageAttachmentResponse,
  PackageCustomer,
  PackageCustomerListResponse,
  PackageDayDetailsType,
  PackageFile,
  PackageListResponse,
  PackageReorder,
  PackageResponse,
  ReorderPackagesRequest,
  UpdatePackageNotesRequest,
  UpdatePackageRequest,
} from "@przezmorze/api-types";
import { przezmorzeApi, throwMessageOnBadRequestStatus } from "../../../api/api";
import type { NoteDTO } from "../../../api/api";

export type {
  AddPackageCustomerRequest,
  CreatePackageRequest,
  PackageCustomer as PackageCustomerResponse,
  PackageFile,
  PackageReorder,
  PackageResponse,
  UpdatePackageRequest,
};

export async function getPackage(packageId: string, abortSignal?: AbortSignal) {
  if (!packageId) {
    return null;
  }
  const response = await przezmorzeApi.get<PackageResponse>(`/packages/${packageId}`, {
    signal: abortSignal,
  });
  return response.data;
}

export async function listPackages(abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.get<PackageListResponse>("/packages", {
    signal: abortSignal,
  });
  return response.data.items;
}

export async function createPackage(body: CreatePackageRequest, abortSignal?: AbortSignal): Promise<PackageResponse> {
  try {
    const response = await przezmorzeApi.post<PackageResponse>("/packages", body, {
      signal: abortSignal,
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updatePackage(
  packageId: string,
  body: UpdatePackageRequest,
  abortSignal?: AbortSignal,
): Promise<PackageResponse> {
  try {
    const response = await przezmorzeApi.put<PackageResponse>(`/packages/${packageId}`, body, {
      signal: abortSignal,
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function deletePackage(packageId: string, abortSignal?: AbortSignal): Promise<void> {
  await przezmorzeApi.delete<void>(`/packages/${packageId}`, {
    signal: abortSignal,
  });
}

export async function updatePackageNotes(
  packageId: string,
  notes: NoteDTO[],
  abortSignal?: AbortSignal,
): Promise<PackageResponse> {
  const body: UpdatePackageNotesRequest = { notes };
  const response = await przezmorzeApi.put<PackageResponse>(`/packages/${packageId}/notes`, body, {
    signal: abortSignal,
  });
  return response.data;
}

export async function addPackageAttachment(
  packageId: string,
  body: FormData,
  abortSignal?: AbortSignal,
): Promise<PackageFile> {
  const response = await przezmorzeApi.post<PackageFile>(`/packages/${packageId}/attachments`, body, {
    signal: abortSignal,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

export async function deletePackageAttachment(
  packageId: string,
  name: string,
  abortSignal?: AbortSignal,
): Promise<PackageFile[]> {
  const response = await przezmorzeApi.delete<DeletePackageAttachmentResponse>(
    `/packages/${packageId}/attachments/${name}`,
    {
      signal: abortSignal,
    },
  );
  return response.data.items;
}

export async function getPackageDoc(
  packageId: string,
  dayDetailsType: PackageDayDetailsType,
  abortSignal?: AbortSignal,
) {
  if (!packageId) {
    return null;
  }
  const response = await przezmorzeApi.get<Blob>(`/packages/${packageId}/doc`, {
    signal: abortSignal,
    headers: { Accept: "application/pdf" },
    params: { dayDetailsType },
    responseType: "blob",
  });
  return response.data;
}

export async function listPackageCustomers(packageId: string, abortSignal?: AbortSignal) {
  if (!packageId) {
    return [];
  }
  const response = await przezmorzeApi.get<PackageCustomerListResponse>(`/packages/${packageId}/customers`, {
    signal: abortSignal,
  });
  return response.data.items;
}

export async function addPackageCustomer(
  packageId: string,
  request: AddPackageCustomerRequest,
  abortSignal?: AbortSignal,
): Promise<PackageCustomer> {
  const response = await przezmorzeApi.post<PackageCustomer>(`/packages/${packageId}/customers`, request, {
    signal: abortSignal,
  });
  return response.data;
}

export async function deletePackageCustomer(packageId: string, customerId: string, abortSignal?: AbortSignal) {
  await przezmorzeApi.delete<void>(`/packages/${packageId}/customers/${customerId}`, {
    signal: abortSignal,
  });
}

export async function reorderPackages(reorders: PackageReorder[], abortSignal?: AbortSignal): Promise<void> {
  const body: ReorderPackagesRequest = { reorders };
  await przezmorzeApi.post<void>(`/packages/reorder`, body, {
    signal: abortSignal,
  });
}
